/**
 * Zukunftsausblick-Karte — der tägliche KI-Makro-Brief mit Szenarien.
 *
 * Zeigt die Kernaussage als 1 Zeile (zugeklappt = schlank auf „Heute");
 * aufgeklappt: Entwicklung ggü. den letzten Briefen, 2–3 Szenarien mit
 * Wahrscheinlichkeit, Gewinner-/Verlierer-Töpfen, Frühindikatoren und
 * Kipp-Signal, plus die bewusst ignorierte Tagesrauschen-Ablage.
 *
 * Leitplanke sichtbar im Footer: KI erklärt — Zahlen bleiben der Anker.
 */

import { Telescope } from 'lucide-react';

import { useDataState } from '@/hooks/use-data-state';
import { TOPF_ETF } from '@/lib/macro-brief';
import { fetchLatestWeeklyLine, type MacroBriefRow } from '@/lib/macro-brief-read';

/** Topf-Chip-Text: Topf-Name + passender ETF (User-Wunsch: „welcher ETF passt dazu"). */
function topfLabel(topf: string): string {
  const etf = TOPF_ETF[topf];
  return etf ? `${topf} · ${etf}` : topf;
}

/** Audit 14.07.: Die KI liefert teils **Markdown-Fett** — roh angezeigt sah
 *  das wie ein Darstellungsfehler aus. Sternchen hier konsequent entfernen. */
function plain(text: string | null | undefined): string {
  return (text ?? '').replace(/\*\*/g, '');
}

interface Props {
  brief: MacroBriefRow | null;
}

export function ZukunftsausblickCard({ brief }: Props) {
  // Optimierung 07.07. — die Sonntags-Wochen-Linie war nur Prompt-Gedächtnis;
  // jetzt sieht der User die lange Linie über dem Tages-Detail.
  const weekly = useDataState({
    queryKey: ['macro-weekly-line'],
    queryFn: () => fetchLatestWeeklyLine(),
    ttlSeconds: 60 * 60,
    isEmpty: (w) => !w,
  });

  if (!brief) return null;

  const datum = new Date(brief.brief_date).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  });

  return (
    <details className="rounded-md border border-zinc-800/80 bg-zinc-900/30 group/ausblick mb-3">
      <summary className="cursor-pointer list-none p-3 min-h-[44px] flex items-start gap-2 hover:bg-zinc-800/40 rounded-md">
        <span className="text-cockpit-accent inline-block transition-transform group-open/ausblick:rotate-90 mt-0.5">
          ▸
        </span>
        <Telescope className="size-4 text-violet-300 shrink-0 mt-0.5" />
        <span className="min-w-0">
          <span className="block text-[10px] uppercase tracking-wider text-zinc-500 font-medium">
            Zukunftsausblick (KI) · {datum}
          </span>
          <span className="block text-sm font-semibold text-zinc-100 leading-snug">
            {plain(brief.kernaussage)}
          </span>
        </span>
      </summary>

      <div className="px-3 pb-3 space-y-3">
        {weekly.state.data && (
          <p className="rounded border border-violet-800/40 bg-violet-950/20 px-2.5 py-1.5 text-[11px] leading-relaxed text-violet-200/90">
            <span className="font-semibold">
              Wochen-Linie (
              {new Date(weekly.state.data.brief_date).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
              })}
              ):{' '}
            </span>
            {plain(weekly.state.data.kernaussage)}
          </p>
        )}
        {brief.entwicklung && (
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-semibold text-zinc-200">Entwicklung: </span>
            {plain(brief.entwicklung)}
          </p>
        )}

        <div className="space-y-2">
          {brief.szenarien.map((s) => (
            <div
              key={s.name}
              className="rounded-md border border-zinc-800/60 bg-zinc-900/40 p-2.5"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-zinc-100">{s.name}</span>
                <span className="rounded-full border border-zinc-700 bg-zinc-800/80 px-2 py-0.5 font-mono text-[10px] text-zinc-200">
                  {s.p} %
                </span>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-300">{plain(s.story)}</p>
              {(s.gewinner.length > 0 || s.verlierer.length > 0) && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {s.gewinner.map((g) => (
                    <span
                      key={g}
                      className="rounded-full border border-emerald-700/50 bg-emerald-950/30 px-2 py-0.5 text-[10px] text-emerald-200"
                    >
                      ▲ {topfLabel(g)}
                    </span>
                  ))}
                  {s.verlierer.map((v) => (
                    <span
                      key={v}
                      className="rounded-full border border-red-800/50 bg-red-950/25 px-2 py-0.5 text-[10px] text-red-300"
                    >
                      ▼ {topfLabel(v)}
                    </span>
                  ))}
                </div>
              )}
              {s.fruehindikatoren.length > 0 && (
                <div className="mt-1.5 text-[11px] text-zinc-400">
                  <span className="font-medium text-zinc-300">Woran ich es früh erkenne: </span>
                  {s.fruehindikatoren.join(' · ')}
                </div>
              )}
              {s.kipp_signal && (
                <div className="mt-0.5 text-[11px] text-amber-200/80">
                  <span className="font-medium">Kippt, wenn: </span>
                  {s.kipp_signal}
                </div>
              )}
            </div>
          ))}
        </div>

        {brief.tagesrauschen && (
          <p className="text-[11px] text-zinc-500">
            <span className="font-medium">Nur Tagesrauschen (bewusst ignoriert): </span>
            {brief.tagesrauschen}
          </p>
        )}
        <p className="text-[10px] text-zinc-600">
          KI erklärt und schätzt Wahrscheinlichkeiten — die gemessenen Geldflüsse bleiben der
          Anker. Kein Kaufsignal.
        </p>
      </div>
    </details>
  );
}
